import React, { useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import Select from "react-select";
import Modal from "../Model";
import BlockForm from "./BlockForm";
import PagePreview from "./PagePreview";
import {
  GET_SERVICE_PAGE, PREVIEW_SERVICE_DETAIL, SAVE_SERVICE_PAGE, PUBLISH_SERVICE_PAGE, ARCHIVE_SERVICE_PAGE,
} from "./graphql";
import { GET_CATALOG, GET_COLLECTIONS } from "../AdminHomeBuilder/graphql";
import { STATUS_STYLES, formatDate } from "../AdminHomeBuilder/labels";
import {
  BLOCK_TYPES, blockSummary, newBlock, newBlockId,
} from "./blocks";

const TAB_LABEL = { Female: "Salon", Male: "Bridal" };

const Toggle = ({ on, onChange, label }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={onChange}
    className={`relative w-10 h-6 rounded-full transition-colors flex-none ${on ? "bg-green-600" : "bg-gray-300"}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-4" : ""}`} />
  </button>
);

const parseBlocks = (s) => { try { return JSON.parse(s || "[]"); } catch (e) { return []; } };

/** Edit one service page: what it is assigned to, its blocks, and a live preview. */
const PageEditor = ({ pageId, pages, autoBlocks, canEdit, onBack, onChanged }) => {
  const pageQuery = useQuery(GET_SERVICE_PAGE, { variables: { id: pageId }, fetchPolicy: "network-only" });
  const female = useQuery(GET_CATALOG, { variables: { gender: "Female" } });
  const male = useQuery(GET_CATALOG, { variables: { gender: "Male" } });
  const collectionsQuery = useQuery(GET_COLLECTIONS);
  const [save, { loading: saving }] = useMutation(SAVE_SERVICE_PAGE);
  const [publish, { loading: publishing }] = useMutation(PUBLISH_SERVICE_PAGE);
  const [archive] = useMutation(ARCHIVE_SERVICE_PAGE);
  const [runPreview, preview] = useLazyQuery(PREVIEW_SERVICE_DETAIL, { fetchPolicy: "network-only" });

  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState(null);
  const [editing, setEditing] = useState(null); // { index, block }
  const [adding, setAdding] = useState(false);
  const [previewId, setPreviewId] = useState(null);

  const page = pageQuery.data?.getServicePage?.page;
  useEffect(() => {
    if (page) {
      setDraft({ ...page, blocks: parseBlocks(page.blocks) });
      setDirty(false);
    }
  }, [page]);

  const catalog = useMemo(() => {
    const tag = (data, gender) => ({
      categories: (data?.getHomeBuilderCatalog?.categories || []).map((c) => ({ ...c, gender })),
      services: (data?.getHomeBuilderCatalog?.services || []).map((s) => ({ ...s, gender })),
    });
    const f = tag(female.data, "Female");
    const m = tag(male.data, "Male");
    return { categories: [...f.categories, ...m.categories], services: [...f.services, ...m.services] };
  }, [female.data, male.data]);

  const categoryOptions = catalog.categories.map((c) => ({ value: c.id, label: `${c.name} (${TAB_LABEL[c.gender]})` }));
  const serviceOptions = catalog.services.map((s) => ({
    value: s.id,
    label: `${s.name}${s.catalogueName && s.catalogueName !== s.name ? ` [${s.catalogueName}]` : ""} · ₹${s.price} (${TAB_LABEL[s.gender]})`,
  }));

  // Preview a service this page applies to, unless the admin picked one.
  const defaultPreviewId = useMemo(() => {
    if (!draft) return null;
    if (draft.serviceIds[0]) return draft.serviceIds[0];
    const inCategory = catalog.services.find((s) => draft.categoryIds.includes(s.categoryId));
    if (inCategory) return inCategory.id;
    const inTab = catalog.services.find((s) => draft.defaultFor.includes(s.gender));
    return inTab ? inTab.id : catalog.services[0]?.id || null;
  }, [draft, catalog.services]);
  const previewServiceId = previewId || defaultPreviewId;

  const input = useMemo(() => (draft ? {
    id: draft.id,
    name: draft.name || "Untitled",
    defaultFor: draft.defaultFor,
    categoryIds: draft.categoryIds,
    serviceIds: draft.serviceIds,
    blocks: JSON.stringify(draft.blocks),
  } : null), [draft]);

  useEffect(() => {
    if (!input || !previewServiceId) return undefined;
    const t = setTimeout(() => runPreview({ variables: { serviceId: previewServiceId, data: input } }), 500);
    return () => clearTimeout(t);
  }, [input, previewServiceId, runPreview]);

  if (!draft) return <div className="p-6 text-sm text-gray-500">{pageQuery.loading ? "Loading…" : pageQuery.data?.getServicePage?.message || "Page not found"}</div>;

  const update = (patch) => { setDraft({ ...draft, ...patch }); setDirty(true); setMessage(null); };
  const setBlocks = (blocks) => update({ blocks });
  const move = (i, d) => { const b = [...draft.blocks]; [b[i], b[i + d]] = [b[i + d], b[i]]; setBlocks(b); };

  const doSave = async () => {
    const res = await save({ variables: { data: input } });
    const out = res.data?.saveServicePage;
    setMessage({ ok: out?.status === "SUCCESS", text: out?.message || "Could not save" });
    if (out?.status === "SUCCESS") { setDirty(false); onChanged(); pageQuery.refetch(); }
    return out?.status === "SUCCESS";
  };
  const doPublish = async () => {
    if (dirty && !(await doSave())) return;
    const res = await publish({ variables: { id: draft.id } });
    const out = res.data?.publishServicePage;
    setMessage({ ok: out?.status === "SUCCESS", text: out?.status === "SUCCESS" ? "Published. Customers see this page now." : out?.message || "Could not publish" });
    onChanged();
    pageQuery.refetch();
  };
  const doArchive = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Unpublish this page? Its services go back to the next page in line (category, tab default, or automatic).")) return;
    const res = await archive({ variables: { id: draft.id } });
    setMessage({ ok: true, text: res.data?.archiveServicePage?.message });
    onChanged();
    pageQuery.refetch();
  };
  const back = () => {
    // eslint-disable-next-line no-alert
    if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return;
    onBack();
  };

  const live = draft.status === "LIVE";
  const ctx = {
    serviceOptions,
    collections: collectionsQuery.data?.getCollections?.collections || [],
    pages,
    pageId: draft.id,
    serviceIds: draft.serviceIds,
    categoryIds: draft.categoryIds,
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3 bg-white border rounded-lg p-3 sticky top-0 z-10">
        <button onClick={back} className="px-3 py-1.5 rounded-md bg-gray-100 text-sm">← All pages</button>
        <input className="border rounded-md px-3 py-1.5 font-semibold flex-1 min-w-[200px]" value={draft.name} disabled={!canEdit} onChange={(e) => update({ name: e.target.value })} aria-label="Page name" />
        <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[draft.status]}`}>{draft.status.toLowerCase()}</span>
        {draft.publishedAt && <span className="text-xs text-gray-500">v{draft.version} · published {formatDate(draft.publishedAt)}</span>}
        {canEdit && (
          <div className="flex gap-2 ml-auto">
            <button onClick={doSave} disabled={!dirty || saving} className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm disabled:opacity-40">{saving ? "Saving…" : live ? "Save (goes live)" : "Save draft"}</button>
            {!live && <button onClick={doPublish} disabled={publishing} className="px-4 py-2 rounded-md bg-[#FBB033] text-sm font-semibold">Publish</button>}
            {live && <button onClick={doArchive} className="px-4 py-2 rounded-md bg-red-50 text-red-700 text-sm">Unpublish</button>}
          </div>
        )}
      </div>
      {message && <div className={`text-sm px-3 py-2 rounded-md ${message.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}>{message.text}</div>}
      {live && dirty && <div className="text-sm px-3 py-2 rounded-md bg-amber-50 text-amber-800">This page is live: saving changes what customers see right away.</div>}

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4 items-start">
        <div className="flex flex-col gap-4">
          <section className="bg-white border rounded-lg p-4 flex flex-col gap-3">
            <div>
              <h2 className="font-semibold">Shown for</h2>
              <p className="text-xs text-gray-500">The most specific live page wins: a service&apos;s own page, then its category&apos;s, then the tab default. Services with none get the automatic page built from their details.</p>
            </div>
            <div className="flex gap-4 text-sm">
              {["Female", "Male"].map((g) => (
                <label key={g} className="flex items-center gap-2">
                  <input type="checkbox" disabled={!canEdit} checked={draft.defaultFor.includes(g)}
                    onChange={(e) => update({ defaultFor: e.target.checked ? [...draft.defaultFor, g] : draft.defaultFor.filter((x) => x !== g) })} />
                  Default for every {TAB_LABEL[g]} service
                </label>
              ))}
            </div>
            <label className="text-sm flex flex-col gap-1">
              <span className="font-medium">Categories</span>
              <Select isMulti isDisabled={!canEdit} options={categoryOptions} value={categoryOptions.filter((o) => draft.categoryIds.includes(o.value))} onChange={(v) => update({ categoryIds: v.map((o) => o.value) })} placeholder="None" />
            </label>
            <label className="text-sm flex flex-col gap-1">
              <span className="font-medium">Services</span>
              <Select isMulti isDisabled={!canEdit} options={serviceOptions} value={draft.serviceIds.map((id) => serviceOptions.find((o) => o.value === id)).filter(Boolean)} onChange={(v) => update({ serviceIds: v.map((o) => o.value) })} placeholder="None" />
            </label>
          </section>

          <section className="bg-white border rounded-lg">
            <div className="flex items-center justify-between p-3 border-b">
              <div>
                <h2 className="font-semibold">Blocks</h2>
                <p className="text-xs text-gray-500">Name, rating, price and ADD always come first. Then these, top to bottom.</p>
              </div>
              {canEdit && <button onClick={() => setAdding(true)} className="px-4 py-2 bg-[#FBB033] rounded-md text-sm font-semibold">+ Add block</button>}
            </div>
            <ol className="divide-y">
              {draft.blocks.map((b, i) => (
                <li key={b.id} className={`flex items-center gap-3 px-3 py-2.5 ${b.isActive === false ? "opacity-50" : ""}`}>
                  <span className="text-xs text-gray-400 w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{b.title || BLOCK_TYPES[b.type]?.label}</div>
                    <div className="text-xs text-gray-500 truncate">{BLOCK_TYPES[b.type]?.label}{blockSummary(b) ? ` · ${blockSummary(b)}` : ""}</div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <Toggle on={b.isActive !== false} label={`Show ${b.title || b.type}`} onChange={() => setBlocks(draft.blocks.map((x, k) => (k === i ? { ...x, isActive: x.isActive === false } : x)))} />
                      <button disabled={i === 0} onClick={() => move(i, -1)} className="px-2 py-1 text-sm disabled:opacity-30" aria-label="Move up">▲</button>
                      <button disabled={i === draft.blocks.length - 1} onClick={() => move(i, 1)} className="px-2 py-1 text-sm disabled:opacity-30" aria-label="Move down">▼</button>
                      <button onClick={() => setEditing({ index: i, block: JSON.parse(JSON.stringify(b)) })} className="px-3 py-1.5 rounded-md bg-gray-100 text-sm">Edit</button>
                      <button onClick={() => setBlocks([...draft.blocks.slice(0, i + 1), { ...JSON.parse(JSON.stringify(b)), id: newBlockId() }, ...draft.blocks.slice(i + 1)])} className="px-2 py-1.5 rounded-md hover:bg-gray-100 text-sm" aria-label="Duplicate block">⧉</button>
                      <button onClick={() => setBlocks(draft.blocks.filter((_, k) => k !== i))} className="px-2 py-1.5 rounded-md hover:bg-red-50 text-red-600 text-sm" aria-label="Remove block">✕</button>
                    </div>
                  )}
                </li>
              ))}
              {!draft.blocks.length && (
                <li className="px-3 py-8 text-center text-sm text-gray-500">
                  No blocks yet.
                  {canEdit && autoBlocks.length > 0 && (
                    <button onClick={() => setBlocks(autoBlocks.map((b) => ({ ...b, id: newBlockId() })))} className="ml-2 underline text-blue-700">Start from the automatic page</button>
                  )}
                </li>
              )}
            </ol>
          </section>
        </div>

        <aside className="bg-white border rounded-lg p-3 xl:sticky xl:top-20">
          <label className="text-sm flex flex-col gap-1 mb-3">
            <span className="font-medium">Preview as</span>
            <Select options={serviceOptions} value={serviceOptions.find((o) => o.value === previewServiceId) || null} onChange={(o) => setPreviewId(o?.value || null)} placeholder="Pick a service" />
          </label>
          <PagePreview
            detail={preview.data?.previewServiceDetail?.detail || preview.previousData?.previewServiceDetail?.detail}
            loading={preview.loading}
            error={preview.data?.previewServiceDetail?.status === "ERROR" ? preview.data.previewServiceDetail.message : null}
          />
        </aside>
      </div>

      {adding && (
        <Modal onClose={() => setAdding(false)} widthClass="sm:w-[760px]">
          <h2 className="text-lg font-semibold mb-3">Add a block</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(BLOCK_TYPES).map(([type, t]) => (
              <button key={type} onClick={() => { const b = newBlock(type); setAdding(false); setEditing({ index: draft.blocks.length, block: b, isNew: true }); }}
                className="text-left border rounded-lg p-3 hover:border-gray-900">
                <div className="font-medium text-sm">{t.label}</div>
                <div className="text-xs text-gray-500">{t.help}</div>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} disableOutsideClick widthClass="sm:w-[760px]">
          <BlockForm block={editing.block} ctx={ctx} onChange={(block) => setEditing({ ...editing, block })} />
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md bg-gray-100">Cancel</button>
            <button
              onClick={() => {
                const blocks = [...draft.blocks];
                if (editing.isNew) blocks.push(editing.block); else blocks[editing.index] = editing.block;
                setBlocks(blocks);
                setEditing(null);
              }}
              className="px-5 py-2 rounded-md bg-[#FBB033] font-semibold"
            >
              {editing.isNew ? "Add block" : "Done"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PageEditor;
